import type { Root as HastRoot } from "hast";
import type { Components as JsxRuntimeComponents } from "hast-util-to-jsx-runtime";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import type { Root as MdastRoot } from "mdast";
import type { Handlers } from "mdast-util-to-hast";
import { Fragment, useMemo, type ReactElement } from "react";
import type { Components } from "react-markdown";
import { jsx, jsxs } from "react/jsx-runtime";
import rehypeRaw from "rehype-raw";
import type { PluggableList } from "unified";
import { unified } from "unified";
import { SourceAttributionLabel } from "~/components/SourceAttributionLabel";
import { cn } from "~/lib/utils";
import { TERMS_PROSE_CLASS } from "~/utils/markdownTerms";
import { rehypeSourceLabels } from "~/utils/rehypeSourceLabels";
import { mdastToHast } from "./mdast/buildHast";
import { rehypeStripMdastOrigin } from "./mdast/provenance";
import { rehypeSanitizeUrls } from "./mdast/rehypeSanitizeUrls";
import {
  rehypeTermHighlight,
  type RehypeTermHighlightOptions,
} from "./mdast/rehypeTermHighlight";
import { applySoftBreaks } from "./mdast/softBreaks";

/**
 * Shared heading component overrides, kept identical to `UnifiedMarkdown` so v1
 * and v2 render headings the same when `withHeadings` is enabled.
 */
const headingComponents: Components = {
  h1: ({ node: _node, ...props }) => (
    <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 {...props} className="text-base font-bold mb-1" />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 {...props} className="text-sm font-medium uppercase mb-1" />
  ),
};

const INACTIVE_TERM_HIGHLIGHT: RehypeTermHighlightOptions = {
  regex: null,
  matcher: null,
};

/** Stable default so the render `useMemo` isn't invalidated every render. */
const NO_EXTRA_REHYPE_PLUGINS: PluggableList = [];

type MdastMarkdownProps = {
  /** Validated MDAST `root` (never mutated — the pipeline operates on a clone). */
  tree: MdastRoot;
  /** Extra className merged with `TERMS_PROSE_CLASS`. */
  className?: string;
  /** Apply custom heading styles (h1, h2, h3). @default false */
  withHeadings?: boolean;
  /** Custom `mdast-util-to-hast` handlers (e.g. the citation `link` handler). */
  toHastHandlers?: Partial<Handlers>;
  /** Term/medication highlighting inputs for the single HAST highlight pass. */
  termHighlight?: RehypeTermHighlightOptions;
  /** Rehype plugins run after source labels (operate on parsed HTML). */
  extraRehypePluginsAfterRaw?: PluggableList;
  /** Additional component overrides (merged after heading + abbr components). */
  components?: Components;
};

/**
 * MDAST → HAST → React renderer mirroring `UnifiedMarkdown` for the v2 report
 * pipeline (§6.1). Owns the pipeline order:
 *
 *   clone + soft breaks → mdast-util-to-hast (allowDangerousHtml) → rehypeRaw →
 *   URL sanitize → term highlight (before source labels) → rehypeSourceLabels →
 *   extra plugins (citation grouping) → strip provenance marker → JSX runtime.
 *
 * The whole derivation is memoized on its inputs so re-renders (accordion
 * expand, Strict Mode double-invoke) never re-run the pipeline or mutate the
 * cached tree.
 */
export const MdastMarkdown = ({
  tree,
  className,
  withHeadings = false,
  toHastHandlers,
  termHighlight = INACTIVE_TERM_HIGHLIGHT,
  extraRehypePluginsAfterRaw = NO_EXTRA_REHYPE_PLUGINS,
  components,
}: MdastMarkdownProps): ReactElement => {
  const rendered = useMemo(() => {
    const initialHast: HastRoot = mdastToHast(
      applySoftBreaks(tree),
      toHastHandlers
    );

    const processor = unified()
      .use(rehypeRaw)
      .use(rehypeSanitizeUrls)
      .use(rehypeTermHighlight, termHighlight)
      .use(rehypeSourceLabels)
      .use(extraRehypePluginsAfterRaw)
      .use(rehypeStripMdastOrigin);

    const finalHast: HastRoot = processor.runSync(initialHast);

    const mergedComponents: Partial<JsxRuntimeComponents> = {
      ...(withHeadings ? headingComponents : {}),
      abbr: SourceAttributionLabel,
      ...components,
    };

    return toJsxRuntime(finalHast, {
      Fragment,
      jsx,
      jsxs,
      components: mergedComponents,
      passNode: true,
    });
  }, [
    tree,
    toHastHandlers,
    termHighlight,
    extraRehypePluginsAfterRaw,
    components,
    withHeadings,
  ]);

  return <div className={cn(TERMS_PROSE_CLASS, className)}>{rendered}</div>;
};
